import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore } from '@/stores/authStore';
import type { EmploymentTypeDto } from '@/types/employmentType';
import styles from './EmploymentTypeModals.module.scss';

// ============================================
// TYPES
// ============================================

interface ViewEmploymentTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: EmploymentTypeDto | null;
  onEdit: () => void;
  onDelete: () => void;
  onAudit?: () => void;
}

// ============================================
// HELPERS
// ============================================

function humanize(value: string): string {
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function BoolBadge({ value }: { value: boolean }) {
  const { t } = useTranslation();
  return (
    <span className={`${styles.boolBadge} ${value ? styles.boolBadgeYes : styles.boolBadgeNo}`}>
      {value ? t('K_YES', 'Yes') : t('K_NO', 'No')}
    </span>
  );
}

// ============================================
// COMPONENT
// ============================================

function ViewEmploymentTypeModal({
  isOpen,
  onClose,
  data,
  onEdit,
  onDelete,
  onAudit,
}: ViewEmploymentTypeModalProps) {
  const { t } = useTranslation();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  if (!data) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={data.name}
      size="md"
      footer={
        <div className={styles.viewFooter}>
          {hasPermission('EMPLOYMENT_TYPES_DELETE') && data.status === 'ACTIVE' && (
            <Button variant="danger" size="sm" onClick={onDelete}>
              {t('K_DEACTIVATE', 'Deactivate')}
            </Button>
          )}
          <div className={styles.viewFooterRight}>
            <Button variant="outline" size="sm" onClick={onClose}>
              {t('K_CLOSE', 'Close')}
            </Button>
            {onAudit && hasPermission('AUDIT_READ') && (
              <Button variant="outline" size="sm" onClick={onAudit}>
                {t('K_AUDIT_TITLE', 'Audit trail')}
              </Button>
            )}
            {hasPermission('EMPLOYMENT_TYPES_UPDATE') && data.status !== 'INACTIVE' && (
              <Button variant="filled" size="sm" onClick={onEdit}>
                {t('K_EDIT', 'Edit')}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className={styles.fieldGrid}>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_NAME', 'Name')}</span>
          <span className={styles.fieldValue}>{data.name}</span>
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_TAX_TYPE', 'Tax Type')}</span>
          <span className={styles.fieldValue}>{humanize(data.taxType)}</span>
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_PAYROLL', 'Payroll')}</span>
          <BoolBadge value={data.payroll} />
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_STATUTORY_BENEFITS', 'Statutory Benefits')}</span>
          <BoolBadge value={data.statutoryBenefits} />
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_INVOICE_WORKFLOW', 'Invoice Workflow')}</span>
          <span className={styles.fieldValue}>{humanize(data.invoiceWorkflow)}</span>
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_INVOICE_WORKFLOW_OVERRIDABLE', 'Invoice Workflow Overridable')}</span>
          <BoolBadge value={data.invoiceWorkflowOverridable} />
        </div>
        <div className={styles.fieldItem}>
          <span className={styles.fieldLabel}>{t('K_LABEL_STATUS', 'Status')}</span>
          <span className={styles.fieldValue}>{data.status}</span>
        </div>
      </div>
    </Modal>
  );
}

export default ViewEmploymentTypeModal;
